/**
 * dev-variant-drawer.js
 *
 * Overflow-based display switching for native Shopify variant options
 * (`.variant-option--drawer-capable`): shows options inline when they fit on a
 * single line, switches to a drawer trigger when they would wrap.
 *
 * Additional swatches (`.dev-as`) always render as a drawer trigger in Liquid —
 * they are not measured here.
 *
 * Detection method: scrollWidth vs container clientWidth.
 * We temporarily force flex-wrap:nowrap on the options container so all items
 * lay out on a single row. scrollWidth then equals the total one-row width.
 * If that exceeds the container's clientWidth (its available rendered width),
 * the options would wrap and we switch to drawer mode.
 *
 * This avoids all offsetTop / offsetParent / getBoundingClientRect quirks — in
 * particular the issue where .variant-option__button-label has position:relative
 * in the base theme CSS, which can make offsetTop unreliable.
 *
 * Drawer buttons dispatch synthetic change events to the radio inputs so the
 * variant-picker custom element can update the product.
 */

// ─── Native variant option overflow + drawer wiring ───────────────────────────

/** @typedef {HTMLElement & { __devVoDrawer?: VariantOptionDrawer }} VoDrawerFieldset */

class VariantOptionDrawer {
  /** @param {VoDrawerFieldset} fieldset — .variant-option--drawer-capable */
  constructor(fieldset) {
    /** @type {VoDrawerFieldset} */
    this.fieldset = fieldset;
    fieldset.__devVoDrawer = this;

    /** @type {Array<{option1:string,option2:string,option3:string,available:boolean}> | null} */
    this._variantsCache = null;

    // Re-compute contextual availability whenever any option in the form changes
    // (e.g. Slats changes → some sizes may become incompatible).
    const form = fieldset.closest('form');
    if (form) {
      form.addEventListener('change', () => {
        // Defer so that other radio states finish updating first.
        requestAnimationFrame(() => this._updateDrawerItemAvailability());
      });
    }

    this._check();
    this._updateDrawerItemAvailability();
    new ResizeObserver(() => this._check()).observe(fieldset);
  }

  /**
   * Reads all product variants from the JSON script tag embedded in the form.
   * @returns {Array<{option1:string,option2:string,option3:string,available:boolean}> | null}
   */
  _getVariants() {
    if (this._variantsCache) return this._variantsCache;
    const form = this.fieldset.closest('form');
    const script = form?.querySelector('script[data-all-variants]');
    if (!script?.textContent) return null;
    try {
      this._variantsCache = JSON.parse(script.textContent);
      return this._variantsCache;
    } catch {
      return null;
    }
  }

  /**
   * Option values that must stay selected while testing this fieldset’s choices.
   * Includes hidden options (e.g. Color via sibling swatches + hide_option_names) from
   * the current variant JSON — otherwise a size looks available if any color has stock.
   * @returns {string[]}
   */
  _getOtherSelectedValues() {
    const form = this.fieldset.closest('form');
    if (!form) return [];
    const myIndex = this.fieldset.dataset.fieldsetIndex;
    /** @type {Map<string, string>} */
    const byIndex = new Map();

    for (const fs of form.querySelectorAll('fieldset[data-fieldset-index]')) {
      if (!(fs instanceof HTMLElement)) continue;
      const idx = fs.dataset.fieldsetIndex;
      if (idx == null) continue;
      const checked = /** @type {HTMLInputElement | null} */ (fs.querySelector('input[type="radio"]:checked'));
      if (checked?.value) byIndex.set(idx, checked.value);
    }

    // Fill gaps from current variant (hidden Color, etc.)
    const picker = this.fieldset.closest('variant-picker');
    for (const script of picker?.querySelectorAll('form script[type="application/json"]') ?? []) {
      if (script.hasAttribute('data-all-variants')) continue;
      try {
        const variant = JSON.parse(script.textContent || '');
        /** @type {string[]} */
        const options = Array.isArray(variant?.options)
          ? variant.options
          : [variant?.option1, variant?.option2, variant?.option3].filter(
              (/** @type {unknown} */ v) => typeof v === 'string' && v
            );
        options.forEach((opt, i) => {
          const key = String(i);
          if (!byIndex.has(key) && opt) byIndex.set(key, opt);
        });
      } catch {
        // ignore malformed JSON
      }
      break;
    }

    /** @type {string[]} */
    const values = [];
    for (const [key, val] of byIndex) {
      if (key !== String(myIndex)) values.push(val);
    }
    return values;
  }

  /**
   * Updates each drawer item's disabled/unavailable state based on whether a compatible
   * available variant exists when combining this item's value with the currently selected
   * values of all other options. This is more precise than Shopify's server-side
   * `product_option_value.available` which only checks global existence.
   */
  _updateDrawerItemAvailability() {
    // Complementary picker reuses this drawer wiring but is not part of the product form.
    if (this.fieldset.hasAttribute('data-dev-cpp')) return;

    // Morph / color-sibling reload may replace the variants JSON — never keep a stale cache.
    this._variantsCache = null;

    const variants = this._getVariants();
    if (!variants) return;
    const otherValues = this._getOtherSelectedValues();
    // Live query in case morph replaced DOM nodes after construction.
    const liveRadios = Array.from(this.fieldset.querySelectorAll('input[type="radio"]'));
    // Items may live in a quick-add-ported host outside this fieldset.
    const drawerRoot = this._getDrawerDialog() ?? this.fieldset;
    const liveItems = Array.from(drawerRoot.querySelectorAll('[data-drawer-item]'));

    for (const item of liveItems) {
      if (!(item instanceof HTMLElement)) continue;
      const inputIdx = parseInt(item.dataset.inputIdx ?? '', 10);
      const radio = liveRadios[inputIdx];
      if (!(radio instanceof HTMLInputElement)) continue;
      const thisValue = radio.value;

      const isCompatible = variants.some((v) => {
        if (!v.available) return false;
        const opts = [v.option1, v.option2, v.option3].filter(Boolean);
        if (!opts.includes(thisValue)) return false;
        return otherValues.every((val) => opts.includes(val));
      });

      item.classList.toggle('dev-as-drawer__link--unavailable', !isCompatible);
      if (!isCompatible) {
        item.setAttribute('disabled', '');
      } else {
        item.removeAttribute('disabled');
      }

      // Sync disabled state on the inline label (labels-row) so CSS cursor works.
      const label = radio.closest('label');
      radio.disabled = !isCompatible;
      radio.dataset.optionAvailable = isCompatible ? 'true' : 'false';
      if (label) {
        if (!isCompatible) {
          label.setAttribute('data-disabled', 'true');
        } else {
          label.removeAttribute('data-disabled');
        }
      }
    }
  }

  _check() {
    const labelsRow = /** @type {HTMLElement | null} */ (
      this.fieldset.querySelector('.variant-option__labels-row')
    );
    if (!labelsRow) {
      this.fieldset.classList.remove('variant-option--use-drawer');
      return;
    }

    const labelCount = labelsRow.querySelectorAll('.variant-option__button-label').length;
    if (labelCount < 2) {
      this.fieldset.classList.remove('variant-option--use-drawer');
      this.fieldset.dataset.vodReady = '';
      return;
    }

    // Temporarily remove use-drawer so the labels-row is visible for measurement.
    const wasDrawer = this.fieldset.classList.contains('variant-option--use-drawer');
    if (wasDrawer) this.fieldset.classList.remove('variant-option--use-drawer');

    // Force a single row, measure the natural one-row width, then restore.
    // This avoids all offsetTop/offsetParent quirks — notably the base theme sets
    // position:relative on .variant-option__button-label which can skew offsetTop.
    const prevWrap = labelsRow.style.flexWrap;
    labelsRow.style.flexWrap = 'nowrap';
    const contentWidth = labelsRow.scrollWidth;
    labelsRow.style.flexWrap = prevWrap;

    // On desktop the title and labels-row share the same row (flex-row), so available width
    // for the labels-row is the fieldset minus title header width and the row gap.
    // Do not use <legend> — it is sr-only.
    const isRowMode = window.innerWidth >= 750;
    const title = this.fieldset.querySelector('[data-variant-option-header]');
    const titleW = isRowMode && title ? title.getBoundingClientRect().width : 0;
    const rowGap = isRowMode ? 16 : 0;
    const raw = this.fieldset.clientWidth - titleW - rowGap;
    const availableWidth = raw > 0 ? raw : this.fieldset.clientWidth;

    const overflows = availableWidth > 0 && contentWidth > availableWidth;
    this.fieldset.classList.toggle('variant-option--use-drawer', overflows);

    // Dismiss skeleton — CSS transitions from gray placeholder to real options/trigger
    this.fieldset.dataset.vodReady = '';
  }

  /**
   * Resolve the option drawer dialog. Quick-add portals the host onto `.quick-add-modal`,
   * so it is no longer a descendant of this fieldset.
   * @returns {HTMLDialogElement | null}
   */
  _getDrawerDialog() {
    const local = this.fieldset.querySelector('dialog');
    if (local instanceof HTMLDialogElement) return local;

    const trigger = this.fieldset.querySelector('.variant-option__drawer-trigger');
    const hostId = trigger?.getAttribute('aria-controls');
    if (!hostId) return null;

    const host = document.getElementById(hostId);
    const ported = host?.querySelector('dialog');
    return ported instanceof HTMLDialogElement ? ported : null;
  }

  /** @param {HTMLElement} item */
  _onItemClick(item) {
    const idx = parseInt(item.dataset.inputIdx ?? '', 10);
    const radio = /** @type {HTMLInputElement | undefined} */ (
      this.fieldset.querySelectorAll('input[type="radio"]')[idx]
    );
    if (!radio) return;
    // Never force-enable: sold-out / incompatible options must stay unselectable.
    if (radio.disabled || radio.dataset.optionAvailable === 'false') return;
    if (item.hasAttribute('disabled') || item.classList.contains('dev-as-drawer__link--unavailable')) {
      return;
    }

    // Close before change → variant:selected sets picker inert.
    // PDP: must use DialogComponent.closeDialog() so body scroll-lock is cleared
    // (native dialog.close() leaves position:fixed → permanent scroll freeze).
    // Quick-add: host.closeDialog is patched to animate + native close without
    // touching the parent modal's lock.
    const dialog = this._getDrawerDialog();
    if (dialog?.open) {
      const dialogComponent = /** @type {{ closeDialog?: () => void } | null} */ (
        dialog.closest('dialog-component')
      );
      if (typeof dialogComponent?.closeDialog === 'function') {
        dialogComponent.closeDialog();
      } else {
        dialog.classList.remove('dialog-visible', 'dialog-closing');
        dialog.close();
      }
    }

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));

    // Quick-add: pin form id immediately so ATC before variant:update finishes
    // cannot submit the Pairs-well-with opener variant.
    const variantId = radio.dataset.variantId;
    if (variantId && this.fieldset.closest('quick-add-dialog')) {
      const idInput = this.fieldset
        .closest('quick-add-dialog')
        ?.querySelector('product-form-component input[name="id"]');
      if (idInput instanceof HTMLInputElement) {
        idInput.value = variantId;
      }
    }

    // Update active state in the drawer list (live nodes after morph / portal)
    const drawerRoot = this._getDrawerDialog() ?? this.fieldset;
    const drawerItems = Array.from(drawerRoot.querySelectorAll('[data-drawer-item]'));
    drawerItems.forEach((el, i) => {
      el.classList.toggle('dev-as-drawer__link--active', i === idx);
    });

    // Update trigger button label to show newly selected value
    const label = item.querySelector('[data-drawer-item-label]');
    const triggerValue = this.fieldset.querySelector('[data-variant-drawer-trigger-value]');
    if (triggerValue && label) {
      const sourceLine = label.querySelector('[data-rolling-text-line]');
      const nextLabel = (sourceLine?.textContent ?? label.textContent)?.trim() ?? '';
      const rollingLines = triggerValue.querySelectorAll('[data-rolling-text-line]');
      if (rollingLines.length > 0) {
        for (const line of rollingLines) {
          line.textContent = nextLabel;
        }
      } else {
        triggerValue.textContent = nextLabel;
      }
    }
  }
}

// ─── Init ──────────────────────────────────────────────────────────────────────

/** @param {HTMLElement | Document} root */
function initInSubtree(root) {
  root.querySelectorAll('.variant-option--drawer-capable').forEach((fieldset) => {
    const voFieldset = /** @type {VoDrawerFieldset} */ (fieldset);
    if (!voFieldset.__devVoDrawer) new VariantOptionDrawer(voFieldset);
  });
}

function initComponents() {
  initInSubtree(document);
}

function recheckAll() {
  document.querySelectorAll('.variant-option--drawer-capable').forEach((fieldset) => {
    /** @type {VoDrawerFieldset} */ (fieldset).__devVoDrawer?._check();
  });
}

// type="module" scripts are always deferred — DOM is ready by the time this runs.
requestAnimationFrame(initComponents);

// After fonts load, silently re-check. Elements are already in correct state at this
// point (skeleton dismissed) — re-check only corrects borderline cases where fallback
// font metrics caused a wrong measurement on the first check.
document.fonts?.ready.then(() => requestAnimationFrame(recheckAll)).catch(() => {});

// Watch for dynamically injected content (e.g. quick-add modal morph). When new
// .variant-option--drawer-capable elements land in the DOM they won't have been
// initialised by the one-time requestAnimationFrame above, so they'd stay in the
// skeleton state forever. The MutationObserver catches those additions and initialises
// them immediately.
const _mo = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.classList.contains('variant-option--drawer-capable')) {
        const voNode = /** @type {VoDrawerFieldset} */ (node);
        if (!voNode.__devVoDrawer) new VariantOptionDrawer(voNode);
      }
      initInSubtree(node);
    }
  }
});
_mo.observe(document.body, { childList: true, subtree: true });

/**
 * Capture-phase delegation for drawer items.
 * Survives morph (new buttons) and top-layer nested <dialog> inside quick-add —
 * per-button listeners on the old nodes were the main “click does nothing” bug.
 */
/**
 * Fieldset for a drawer item/host. Quick-add portals the host out of the fieldset,
 * so walk via aria-controls → trigger when closest(.variant-option) fails.
 * @param {Element} from
 * @returns {VoDrawerFieldset | null}
 */
function resolveVoDrawerFieldset(from) {
  const local = /** @type {VoDrawerFieldset | null} */ (from.closest('.variant-option--drawer-capable'));
  if (local) return local;

  const host = from.closest('dialog-component[data-quick-add-variant-drawer], dialog-component.dev-drawer');
  const hostId = host?.id;
  if (!hostId) return null;

  const trigger = document.querySelector(
    `.variant-option__drawer-trigger[aria-controls="${CSS.escape(hostId)}"]`
  );
  return /** @type {VoDrawerFieldset | null} */ (trigger?.closest('.variant-option--drawer-capable') ?? null);
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.variant-option__drawer-trigger');
    if (trigger instanceof HTMLElement) {
      const fieldset = resolveVoDrawerFieldset(trigger);
      if (fieldset) {
        if (!fieldset.__devVoDrawer) new VariantOptionDrawer(fieldset);
        fieldset.__devVoDrawer?._updateDrawerItemAvailability();
      }
      return;
    }

    const item = event.target.closest('[data-drawer-item]');
    if (!(item instanceof HTMLElement)) return;

    const fieldset = resolveVoDrawerFieldset(item);
    if (!fieldset) return;

    if (!fieldset.__devVoDrawer) new VariantOptionDrawer(fieldset);

    // Recompute right before accepting the click (color may have changed via siblings).
    fieldset.__devVoDrawer?._updateDrawerItemAvailability();

    if (item.hasAttribute('disabled') || item.classList.contains('dev-as-drawer__link--unavailable')) {
      return;
    }

    fieldset.__devVoDrawer?._onItemClick(item);
  },
  true
);

// ─── Variant fetch lock ────────────────────────────────────────────────────────
// While the section-rendering fetch is in flight, set the nearest <variant-picker>
// to inert so users can't trigger a second change before the DOM morphs.
// Safety timeout: if variant:update never fires (network error / 5xx), release
// after 10 s so the picker doesn't stay permanently disabled.

/** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} */
const _inertTimers = new WeakMap();

document.addEventListener('variant:selected', (e) => {
  const picker = /** @type {HTMLElement | null} */ (
    e.target instanceof HTMLElement ? e.target.closest('variant-picker') : null
  );
  if (!picker) return;

  // Size drawer may be nested in the picker or ported onto `.quick-add-modal`.
  // Prefer closeDialog() so PDP scroll-lock is released; quick-add hosts patch it.
  const dialogsToClose = new Set(
    /** @type {HTMLDialogElement[]} */ ([...picker.querySelectorAll('dialog[open]')])
  );
  const quickAdd = picker.closest('quick-add-dialog');
  if (quickAdd) {
    for (const nested of quickAdd.querySelectorAll(
      'dialog-component[data-qa-drawer-ported] dialog[open], dialog-component[data-quick-add-variant-drawer] dialog[open]'
    )) {
      if (nested instanceof HTMLDialogElement) dialogsToClose.add(nested);
    }
  }
  for (const nested of dialogsToClose) {
    const dialogComponent = /** @type {{ closeDialog?: () => void } | null} */ (
      nested.closest('dialog-component')
    );
    if (typeof dialogComponent?.closeDialog === 'function') {
      dialogComponent.closeDialog();
    } else {
      nested.classList.remove('dialog-visible', 'dialog-closing');
      nested.close();
    }
  }

  picker.inert = true;
  clearTimeout(_inertTimers.get(picker));
  _inertTimers.set(picker, setTimeout(() => { picker.inert = false; }, 10_000));
});

document.addEventListener('variant:update', (e) => {
  const picker = /** @type {HTMLElement | null} */ (
    e.target instanceof HTMLElement ? e.target.closest('variant-picker') : null
  );
  if (!picker) return;
  clearTimeout(_inertTimers.get(picker));
  picker.inert = false;
});
